// filepath: apps/web/src/features/observability/widgets/ErrorRateSparkline.tsx
//
// FR-UI-DASH-1 AC-1 widget 2 -- 7-day per-hour error rate sparkline.
//
// Renders an inline SVG sparkline (no third-party chart library;
// FR-UI-NFR-2 -- no client-side eval). The data points are
// server-emitted; the SPA scales them to the SVG viewport
// deterministically.
//
// INV-9: numeric values rendered as text nodes (the title tooltip
// and the inline numeric caption). No `dangerouslySetInnerHTML`.
// INV-1: tenant scoping is server-side; the `summary` envelope is
// already tenant-filtered (INV-2 -- server strips unauthorised
// fields).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { DrilldownLink } from "../DrilldownLink";
import type { ErrorRatePoint, TenantMetricsSummary } from "../types";

export interface ErrorRateSparklineProps {
  summary: TenantMetricsSummary;
  onDrillDown?: (params: { from: string; to: string; metric: string }) => void;
}

/** Width / height of the inline SVG sparkline (px). Tunable via
 *  CSS if the design system needs different proportions. */
const VIEW_W = 320;
const VIEW_H = 60;
const PADDING = 4;

export function ErrorRateSparkline({
  summary,
  onDrillDown,
}: ErrorRateSparklineProps): React.ReactElement {
  const { t } = useTranslation();

  const points: ErrorRatePoint[] = summary.error_rate_sparkline ?? [];
  const path = buildSparklinePath(points);

  const toIso = new Date().toISOString();
  const fromIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  return (
    <article
      className="observability-widget observability-widget-error-rate"
      data-testid="observability-widget-error-rate"
      aria-labelledby="observability-widget-error-rate-title"
      data-tenant-scope="self"
    >
      <h2
        id="observability-widget-error-rate-title"
        className="observability-widget-title"
      >
        {t(
          "observability.dashboard.widget.error_rate.title",
          "Error rate (last 7 days)",
        )}
      </h2>
      {points.length === 0 ? (
        <p
          className="observability-widget-empty"
          data-testid="observability-widget-error-rate-empty"
        >
          {t(
            "observability.dashboard.widget.error_rate.empty",
            "No recent step activity.",
          )}
        </p>
      ) : (
        <svg
          className="observability-sparkline"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          data-testid="observability-widget-error-rate-svg"
          role="img"
          aria-label={t(
            "observability.dashboard.widget.error_rate.sparkline_aria",
            "Per-hour error rate sparkline",
          )}
        >
          <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} />
        </svg>
      )}
      <DrilldownLink
        from={fromIso}
        to={toIso}
        metric="error_rate"
        onNavigate={onDrillDown}
        label={t(
          "observability.dashboard.widget.drill_down",
          "View failed instances",
        )}
      />
    </article>
  );
}

/** Build an SVG path string from the points array. Empty data
 *  yields an empty string. The path uses simple polyline semantics
 *  (M x,y L x,y ...) -- no curves, no third-party helpers. */
function buildSparklinePath(points: ErrorRatePoint[]): string {
  if (points.length === 0) return "";
  const maxRate = points.reduce((m, p) => Math.max(m, p.error_rate_pct), 0);
  const denomX = Math.max(points.length - 1, 1);
  const denomY = maxRate === 0 ? 1 : maxRate;
  const innerW = VIEW_W - PADDING * 2;
  const innerH = VIEW_H - PADDING * 2;
  let path = "";
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const x = PADDING + (i / denomX) * innerW;
    const y = PADDING + (1 - p.error_rate_pct / denomY) * innerH;
    path +=
      i === 0
        ? `M ${x.toFixed(2)} ${y.toFixed(2)}`
        : ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  return path;
}
